/**
 * API wrapper for all backend calls.
 * Automatically attaches the Azure AD access token to every request.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * Core fetch wrapper — attaches Bearer token and handles JSON.
 *
 * @param {string} endpoint - Path relative to API base, e.g. '/getMyReports'
 * @param {object} options  - fetch options (method, body, etc.)
 * @param {string} token    - Azure AD access token
 */
async function apiFetch(endpoint, options = {}, token) {
  const url = `${API_BASE_URL}${endpoint}`;

  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };

  const response = await fetch(url, { ...options, headers });

  if (!response.ok) {
    let errorBody;
    try {
      errorBody = await response.json();
    } catch {
      errorBody = { error: 'UNKNOWN', message: response.statusText };
    }
    const err = new Error(errorBody.message || errorBody.error || 'API error');
    err.status = response.status;
    err.code = errorBody.error;
    throw err;
  }

  // No-content responses
  if (response.status === 204) {
    return null;
  }

  return response.json();
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Fetch the list of reports the current user has access to.
 */
export async function getMyReports(token) {
  return apiFetch('/getMyReports', { method: 'GET' }, token);
}

/**
 * Request an embed token for a specific report.
 */
export async function getEmbedToken(token, { reportId, workspaceId }) {
  return apiFetch(
    '/getEmbedToken',
    {
      method: 'POST',
      body: JSON.stringify({ reportId, workspaceId }),
    },
    token
  );
}

// ─── Admin API ────────────────────────────────────────────────────────────────

export async function adminGetUsers(token) {
  return apiFetch('/manage/getUsers', { method: 'GET' }, token);
}

export async function adminUpsertUser(token, userData) {
  return apiFetch(
    '/manage/upsertUser',
    { method: 'POST', body: JSON.stringify(userData) },
    token
  );
}

export async function adminGetPermissions(token, userId) {
  return apiFetch(`/manage/getPermissions?userId=${encodeURIComponent(userId)}`, { method: 'GET' }, token);
}

export async function adminUpsertPermission(token, permissionData) {
  return apiFetch(
    '/manage/upsertPermission',
    { method: 'POST', body: JSON.stringify(permissionData) },
    token
  );
}

export async function adminDeletePermission(token, { userId, reportId }) {
  return apiFetch(
    '/manage/deletePermission',
    { method: 'DELETE', body: JSON.stringify({ userId, reportId }) },
    token
  );
}

export async function adminGetWorkspacesReports(token) {
  return apiFetch('/manage/getWorkspacesReports', { method: 'GET' }, token);
}

export async function adminGetDatasetRoles(token, { workspaceId, datasetId }) {
  return apiFetch(
    `/manage/getDatasetRoles?workspaceId=${encodeURIComponent(workspaceId)}&datasetId=${encodeURIComponent(datasetId)}`,
    { method: 'GET' },
    token
  );
}

export async function adminGetLogs(token, { userId, limit } = {}) {
  const params = new URLSearchParams();
  if (userId) {
    params.set('userId', userId);
  }
  if (limit) {
    params.set('limit', limit.toString());
  }
  const qs = params.toString();
  return apiFetch(`/manage/getLogs${qs ? `?${qs}` : ''}`, { method: 'GET' }, token);
}
